import React, { useState, useEffect } from "react";
import { Form, Input, Typography, Button, Row, Space, message, Col, Modal } from "antd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import FAQsApi from "../../services/faqs";
import CustomUploadAdapter from "./CustomUploadAdapter";
import FaqMobilePreviewPage from "./FaqMobilePreview";

const { Text } = Typography;

const FaqCreateForm: React.FC<{ mode?: string }> = () => {
    const [form] = Form.useForm();
    const [answer, setAnswer] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [messageApi, contextHolder] = message.useMessage();
    const faq = location.state?.faq;
    const [editorInstance, setEditorInstance] = useState<any>(null);
    //const mode = location.state?.mode || "edit";
    const [editorData, setEditorData] = useState<string>("");
    const [previewVisible, setPreviewVisible] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any>({
        question: "",
        answer: "",
    });
    const [editorReady, setEditorReady] = useState(false);
    const [mode, setMode] = useState(location.state?.mode || "create");
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

    const sanitizeFileName = (fileName: string): string => {
        if (!fileName) return fileName;
        return fileName
            .replace(/\s+/g, '_')           // Replace spaces with underscores
            .replace(/[^\w.-]/g, '_')       // Replace special chars with underscores
            .toLowerCase();
    };

    const normalizeUrl = (url: string): string => {
        if (!url) return url;
        try {
            // Decode URL-encoded characters like %20 (space)
            const decoded = decodeURIComponent(url);
            // Remove quotes and trim
            return decoded.replace(/["']/g, '').trim();
        } catch (error) {
            return url.replace(/["']/g, '').trim();
        }
    };
    // ============================================
    // HELPER FUNCTIONS
    // ============================================

    const extractImageUrls = (htmlContent: string): string[] => {
        if (!htmlContent) return [];
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            const images = doc.querySelectorAll('img');
            return Array.from(images).map(img => normalizeUrl(img.src));
        } catch (error) {
            console.error('Error extracting image URLs:', error);
            return [];
        }
    };

    const extractImageData = (htmlContent: string): Array<{ url: string, id?: string }> => {
        if (!htmlContent) return [];
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            const images = doc.querySelectorAll('img');
            return Array.from(images).map(img => ({
                url: normalizeUrl(img.src),
                id: img.getAttribute('data-imgid') || undefined
            }));
        } catch (error) {
            console.error('Error extracting image data:', error);
            return [];
        }
    };

    const replaceBlobUrlsWithActualUrls = (
        html: string,
        mappings: { blobUrl: string; actualUrl: string; id: string }[]
    ) => {
        if (!html || !mappings.length) return html;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const images = doc.querySelectorAll("img");

        images.forEach((img) => {
            let src = normalizeUrl(img.getAttribute("src") || '');
            if (!src) return;

            const mapping = mappings.find((m) => {
                const cleanBlob = normalizeUrl(m.blobUrl);
                return src === cleanBlob;
            });

            if (mapping) {
                const cleanActual = normalizeUrl(mapping.actualUrl);
                img.setAttribute("src", cleanActual);
                img.setAttribute("data-imgid", mapping.id);
            }
        });

        return doc.body.innerHTML;
    };

    const processDescriptionImages = (description: string, images: any[]) => {
        if (!description || !images || images.length === 0) {
            return description;
        }

        let processed = description;

        // Check if images already exist in description
        const hasValidImages = images.some((img: any) => {
            const normalizedFileUrl = normalizeUrl(img.fileUrl);
            const normalizedDescription = normalizeUrl(processed);
            return normalizedDescription.includes(normalizedFileUrl);
        });

        if (hasValidImages) {
            //console.log("Images already properly embedded");
            return processed;
        }

        // Remove any blob URLs
        processed = processed.replace(/blob:[^"'\s]+/g, '');

        // If no images in description but we have images, add them
        const hasImageTags = /<img\s+[^>]*src=/.test(processed);
        if (!hasImageTags) {
            const imageHtml = images
                .map((img: any) => {
                    return `<figure class="image">
                    <img src="${img.fileUrl}" 
                         alt="${sanitizeFileName(img.fileName)}" 
                         data-imgid="${img.id}"
                         style="max-width:100%;height:auto;" />
                </figure>`;
                })
                .join("");
            return `${processed}<div>${imageHtml}</div>`;
        }

        return processed;
    };

    // ============================================
    // INITIALIZE WINDOW STORAGE ON COMPONENT MOUNT
    // ============================================
    useEffect(() => {
        // CRITICAL: Initialize arrays BEFORE anything else
        (window as any).serverImages = [];
        (window as any).pendingFaqImages = [];
        (window as any).blobUrlMappings = [];
        (window as any).currentEditorContent = "";

    }, []); // Run once on mount

    useEffect(() => {
        if ((mode === "edit" || mode === "view") && faq) {
            const images = faq.faqImagesResponses || [];
            let processedAnswer = faq.answer ?? "";

            // console.log("FAQ Images from server:", images);
            //console.log("Original answer:", processedAnswer);

            // Check if answer already has valid image URLs
            const hasValidImages = images.length > 0 && images.some((img: any) => {
                const normalizedUrl = normalizeUrl(img.fileUrl);
                const normalizedAnswer = normalizeUrl(processedAnswer);
                return normalizedAnswer.includes(normalizedUrl);
            });

            if (hasValidImages) {
                //console.log("Answer already has valid image URLs");
            } else if (images.length > 0) {
                //console.log("Adding all images to answer");

                // Remove any blob URLs that might exist
                processedAnswer = processedAnswer.replace(/src="blob:[^"]*"/g, '');

                // Build HTML for ALL images
                const imageHtml = images
                    .map((img: any) => {
                        return `<figure class="image">
                        <img src="${img.fileUrl}" 
                             alt="${sanitizeFileName(img.fileName)}" 
                             data-imgid="${img.id}"
                             style="max-width:100%;height:auto;" />
                    </figure>`;
                    })
                    .join("");

                // Append all images to answer
                processedAnswer = `${processedAnswer}<div>${imageHtml}</div>`;
            }

            //console.log("Final processed answer:", processedAnswer);

            // Update form & editor
            form.setFieldsValue({
                question: faq.question,
                answer: processedAnswer,
            });

            setAnswer(processedAnswer);
            setEditorData(processedAnswer);

            // Initialize window-level tracking with normalized data
            (window as any).serverImages = images.map((img: any) => ({
                id: img.id,
                fileName: sanitizeFileName(img.fileName),
                fileUrl: normalizeUrl(img.fileUrl),
                fileSize: img.fileSize || 0,
            }));

            (window as any).pendingFaqImages = [];
            (window as any).blobUrlMappings = [];
            (window as any).currentEditorContent = processedAnswer;

            //console.log("Server images stored:", (window as any).serverImages);
        }
    }, [mode, faq, form]);


    // ============================================
    // HANDLE EDITOR CHANGES
    // ============================================
    const handleDescriptionChange = (_: any, editor: any) => {
        const data = editor.getData();

        (window as any).currentEditorContent = data;
        setEditorData(data);
        setAnswer(data);

        // Track deleted images in edit mode
        if (mode === 'edit' && (window as any).serverImages) {
            const currentUrls = extractImageUrls(data);
            const previousUrls = extractImageUrls(editorData);
            const deletedUrls = previousUrls.filter(url => !currentUrls.includes(url));

            if (deletedUrls.length > 0) {
                //console.log("Deleted image URLs:", deletedUrls);

                (window as any).serverImages = (window as any).serverImages.filter(
                    (img: any) => !deletedUrls.some(deleted =>
                        normalizeUrl(deleted) === normalizeUrl(img.fileUrl)
                    )
                );

                const blobMappings = (window as any).blobUrlMappings || [];
                const pendingImages = (window as any).pendingFaqImages || [];

                const updatedPending: File[] = [];
                const updatedMappings: any[] = [];

                pendingImages.forEach((file: File, index: number) => {
                    const mapping = blobMappings[index];
                    if (mapping && !deletedUrls.some(deleted =>
                        normalizeUrl(deleted) === normalizeUrl(mapping.blobUrl)
                    )) {
                        updatedPending.push(file);
                        updatedMappings.push(mapping);
                    }
                });

                (window as any).pendingFaqImages = updatedPending;
                (window as any).blobUrlMappings = updatedMappings;
            }
        }

        form.setFieldsValue({ answer: data });
        form.validateFields(['answer']).catch(() => { });
        setPreviewData((prev: any) => ({ ...prev, answer: data }));
    };



    // ============================================
    // UNIFIED SAVE FUNCTION
    // ============================================
    const handleSave = async () => {
        try {
            setLoading(true);
            //  Validate required fields (always question + answer)
            try {
                await form.validateFields(["question", "answer"]);
            } catch {
                messageApi.error("Mandatory fields can’t be empty");
                setLoading(false);
                return;
            }

            const values = form.getFieldsValue();
            const currentAnswer = editorData?.trim() || answer?.trim() || "";

            //  Ensure answer is not empty (even if only HTML tags)
            if (!currentAnswer || currentAnswer.replace(/<[^>]*>/g, "").trim() === "") {
                messageApi.error("Mandatory fields can’t be empty");
                setLoading(false);
                return;
            }

            //  Get image data
            const pendingImages: File[] = (window as any).pendingFaqImages || [];
            const blobMappings = (window as any).blobUrlMappings || [];
            const serverImages = (window as any).serverImages || [];

            let finalAnswer = currentAnswer;
            let faqId = faq?.id || null;

            // =====================================
            //  STEP 1: CREATE or UPDATE FAQ
            // =====================================
            if (mode === "edit" && faqId) {
                // --- EDIT MODE ---
                const currentImageData = extractImageData(currentAnswer);
                const serverImages = (window as any).serverImages || [];

                //console.log("Current images in editor:", currentImageData);
                // console.log("Server images:", serverImages);

                // Find which server images are still present (using normalized URLs)
                const remainingServerImageIds = serverImages
                    .filter((img: any) =>
                        currentImageData.some(imgData => {
                            const normalizedImgUrl = normalizeUrl(imgData.url);
                            const normalizedServerUrl = normalizeUrl(img.fileUrl);
                            return normalizedImgUrl === normalizedServerUrl || imgData.id === img.id;
                        })
                    )
                    .map((img: any) => img.id);

                //console.log("Remaining server image IDs:", remainingServerImageIds);

                const uploadedMappings: { id: string; blobUrl: string; actualUrl: string }[] = [];

                // Upload new images (those with blob URLs)
                if (pendingImages.length > 0) {
                    // console.log("Uploading new images:", pendingImages.length);
                    for (let i = 0; i < pendingImages.length; i++) {
                        const file = pendingImages[i];
                        const mapping = blobMappings[i];

                        // Normalize blob URL for comparison
                        const normalizedBlobUrl = normalizeUrl(mapping?.blobUrl || '');
                        const normalizedAnswer = normalizeUrl(currentAnswer);

                        if (mapping?.blobUrl && normalizedAnswer.includes(normalizedBlobUrl)) {
                            // console.log("Uploading image:", file.name);

                            // Sanitize filename before upload
                            const sanitizedFile = new File(
                                [file],
                                sanitizeFileName(file.name),
                                { type: file.type }
                            );

                            const uploadResponse = await FAQsApi.uploadFaqImage(faqId, sanitizedFile);
                            uploadedMappings.push({
                                id: uploadResponse.id,
                                blobUrl: mapping.blobUrl,
                                actualUrl: uploadResponse.fileUrl,
                            });
                        }
                    }
                }

                // Replace blob URLs with actual URLs
                finalAnswer = replaceBlobUrlsWithActualUrls(finalAnswer, uploadedMappings);

                // Combine all image IDs (existing + newly uploaded)
                const allImageIds = [
                    ...remainingServerImageIds,
                    ...uploadedMappings.map((m) => m.id),
                ];

                //console.log("Final image IDs to send:", allImageIds);

                const updatePayload = {
                    id: faqId,
                    question: values.question,
                    answer: finalAnswer,
                    faqImagesIDs: allImageIds,
                    status: 1,
                };

                //console.log("Update payload:", updatePayload);
                await FAQsApi.updateFaq(updatePayload);
            }
            else {
                // --- CREATE MODE ---
                const createPayload = {
                    question: values.question,
                    answer: currentAnswer,
                    status: 1, // Always publish
                };

                const response = await FAQsApi.createFaq(createPayload);
                faqId = response?.id;

                if (!faqId) {
                    messageApi.error("Failed to create FAQ.");
                    setLoading(false);
                    return;
                }

                const uploadedMappings: { blobUrl: string; actualUrl: string; id: string }[] = [];

                // Upload images
                if (pendingImages.length > 0) {
                    for (let i = 0; i < pendingImages.length; i++) {
                        const file = pendingImages[i];
                        const mapping = blobMappings[i];

                        if (mapping?.blobUrl && currentAnswer.includes(mapping.blobUrl)) {
                            const uploadResponse = await FAQsApi.uploadFaqImage(faqId, file);
                            uploadedMappings.push({
                                blobUrl: mapping.blobUrl,
                                actualUrl: uploadResponse.fileUrl,
                                id: uploadResponse.id,
                            });
                        }
                    }
                }

                // Replace blob URLs if images were uploaded
                if (uploadedMappings.length > 0) {
                    finalAnswer = replaceBlobUrlsWithActualUrls(currentAnswer, uploadedMappings);

                    const updateAnswerPayload = {
                        id: faqId,
                        answer: finalAnswer?.trim() || "",
                    };

                    await FAQsApi.updateFinalAnswerFaq(updateAnswerPayload);
                }
            }

            //  Success message
            messageApi.success(mode === "edit" ? "FAQ updated successfully!" : "FAQ added successfully!");

            // Cleanup and redirect
            form.resetFields();
            setEditorData("");
            setAnswer("");
            (window as any).serverImages = [];
            (window as any).pendingFaqImages = [];
            (window as any).blobUrlMappings = [];
            (window as any).currentEditorContent = "";

            setTimeout(() => {
                navigate(-1);
            }, 1000);

        } catch (err: any) {
            console.error("Save error:", err);
            const backendMessage =
                err?.response?.data?.Message ||
                err?.response?.data?.message ||
                "Something went wrong while saving.";
            messageApi.error(backendMessage);
        } finally {
            setLoading(false);
            setIsPublishModalOpen(false);
        }
    };

      const DiscardChanges = () => {
        setIsDiscardModalOpen(true);
    };


    // ============================================
    // UI HELPER FUNCTIONS
    // ============================================
    const getPlainTextLength = () => {
        const div = document.createElement("div");
        div.innerHTML = answer || "";
        return div.textContent?.trim().length || 0;
    };

    const getTextColor = () => {
        const count = getPlainTextLength();
        return count <= 400 ? "green" : count <= 500 ? "orange" : "red";
    };

    const openPreview = () => {
        setPreviewData({
            question: form.getFieldValue("question") || "",
            answer: form.getFieldValue("answer") || "",
        });
        setPreviewVisible(true);
    };

    const normalizedMode = (mode || "").toLowerCase();

    const pageTitle =
        normalizedMode.includes("edit")
            ? "Edit FAQ"
            : normalizedMode.includes("view")
                ? "View FAQ"
                : "Create FAQ";
    return (
        <>
            {contextHolder}

            {/* Loading Overlay */}
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 2000,
                    }}
                >
                    <img
                        src="/images/newLoading.gif"
                        alt="Loading..."
                        style={{ width: 200, height: 150 }}
                    />
                </div>
            )}

            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Typography.Title level={3} style={{ margin: 0, color: "#41787C" }}>
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined style={{ fontSize: 18, fontWeight: "bold", color: "#41787C", marginRight: 10 }} />}
                            onClick={() => navigate(-1)}
                            style={{ marginBottom: 10, padding: 0 }}
                        />
                        {pageTitle}
                    </Typography.Title>
                </Col>
                {mode === "view" && (
                    <Col>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => setMode("edit")}
                            className="submit-btn btn-publish"
                        >
                            Edit
                        </Button>
                    </Col>
                )}
            </Row>

            {/* Form */}
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                initialValues={{ question: "", answer: "" }}
            >
                {/* Question Field */}
                <Form.Item
                    label={<span style={styles.Questionlabel}>Question<span style={{ color: "red", marginLeft: 4 }}>*</span></span>}
                    name="question"
                    rules={[{ required: true, message: "Please enter question" }]}

                >
                    <Input
                        placeholder="Enter question"
                        style={{ height: 48, borderRadius: 10 }}
                        disabled={mode === "view"}
                    />
                </Form.Item>

                {/* Answer Field */}
                <Form.Item
                    label={<span style={styles.Questionlabel}>Answer<span style={{ color: "red", marginLeft: 4 }}>*</span></span>}
                    required={true}
                    name="answer"
                    // rules={[{ validator: validateTextField("Answer", 10000, true) }]}
                    rules={[{ required: true, message: "Please enter answer" }]}
                    validateTrigger={["onChange", "onBlur"]}
                    style={{ marginBottom: 0 }}
                >
                    <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                        <CKEditor
                            editor={ClassicEditor as any}
                            disabled={mode === "view"}
                            config={{
                                allowedContent: true, //  allow all HTML
                                extraAllowedContent: '*(*)', //  allow all attributes
                                placeholder: "Enter answer",
                                toolbar: [
                                    "heading", "|",
                                    "bold", "italic", "link", "bulletedList", "numberedList", "|",
                                    "blockQuote", "undo", "redo", "imageUpload",
                                    "alignment",
                                    "imageStyle:alignLeft", "imageStyle:alignCenter", "imageStyle:alignRight",
                                ],
                                image: {
                                    toolbar: [
                                        "imageStyle:alignLeft",
                                        "imageStyle:alignCenter",
                                        "imageStyle:alignRight",
                                        "imageStyle:full",
                                    ],
                                    resizeUnit: "%",
                                    resizeOptions: [
                                        { name: "resizeImage:original", label: "Original", value: null },
                                        { name: "resizeImage:25", label: "25%", value: "25" },
                                        { name: "resizeImage:50", label: "50%", value: "50" },
                                        { name: "resizeImage:75", label: "75%", value: "75" },
                                    ],
                                },
                                link: {
                                    decorators: {
                                        isExternal: {
                                            mode: 'manual',
                                            label: 'Open in new tab',
                                            attributes: {
                                                target: '_blank',
                                                rel: 'noopener noreferrer'
                                            }
                                        }
                                    }
                                }
                            }}
                            data={editorData}
                            onReady={(editor) => {
                                setEditorInstance(editor);
                                setEditorReady(true); //  mark as ready

                                // Ensure globals exist
                                if (!(window as any).pendingFaqImages) (window as any).pendingFaqImages = [];
                                if (!(window as any).blobUrlMappings) (window as any).blobUrlMappings = [];
                                if (!(window as any).serverImages) (window as any).serverImages = [];

                                editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => {
                                    return new CustomUploadAdapter(loader);
                                };

                                (window as any).currentEditorContent = editorData || "";
                            }}
                            onChange={handleDescriptionChange}
                        />

                        <style>
                            {`
                            .ck-editor__editable_inline {
                                min-height: 200px;
                                max-height: 400px;
                                overflow-y: auto;
                                background-color: ${mode === "view" ? "#f0f0f0" : "#ffffff"};
                                color: ${mode === "view" ? "#6b6969" : "#000000"};
                            }

                            .ck-content img {
                                width: 200px !important;
                                height: 200px !important;
                                object-fit: contain;
                                display: block;
                                margin: 10px auto !important;
                            }

                            /* Allow scrolling but prevent editing */
                            ${mode === "view" ? `
                                .ck-toolbar {
                                display: none !important;
                                }
                                .ck-editor__editable {
                                pointer-events: auto !important; /* enable scrolling */
                                user-select: text !important; /* allow text selection */
                                cursor: default !important;
                                }
                                .ck-editor__editable[contenteditable="true"] {
                                pointer-events: auto !important;
                                user-select: text !important;
                                }
                                .ck-editor__editable_inline[contenteditable="true"] {
                                pointer-events: auto !important;
                                }
                                .ck-blurred[contenteditable="true"],
                                .ck-focused[contenteditable="true"] {
                                pointer-events: auto !important;
                                }
                                .ck-editor__editable[contenteditable="true"] * {
                                pointer-events: auto !important;
                                }
                                .ck-editor__editable_inline {
                                overflow-y: auto !important;
                                }
                            ` : ""}

                            .ck-powered-by,
                            .ck-powered-by-ckeditor,
                            [class*="ck-powered-by"],
                            .ck-editor__main .ck-powered-by {
                                display: none !important;
                            }
`}
                        </style>
                    </div>
                    <Text
                        style={{
                            fontSize: "12px",
                            marginTop: "6px",
                            display: "block",
                            color: getTextColor(),
                            fontWeight: 500,
                        }}
                    >
                        Count: {getPlainTextLength()}
                    </Text>
                </Form.Item>

                {/* Action Buttons */}
                <Row gutter={[16, 16]} justify="end" style={{ marginTop: 24 }}>

                    <Col xs={24} sm={4} md="auto">
                        <Button onClick={openPreview} style={{ width: "100%" }} className="submit-btn">
                            Mobile Preview
                        </Button>
                    </Col>
                    {mode !== "view" && (
                        <>
                            <Col xs={24} sm={4} md="auto">
                                <Button onClick={() => DiscardChanges()} style={{ width: "100%" }} className="submit-btn">
                                    Discard
                                </Button>
                            </Col>
                            {/* Discard Confirmation Modal */}
                            <Modal
                                open={isDiscardModalOpen}
                                onCancel={() => setIsDiscardModalOpen(false)}
                                footer={null}
                                centered
                                closable={false}
                                bodyStyle={{
                                    textAlign: "center",
                                    padding: "24px 16px",
                                    borderRadius: 12,
                                }}
                            >
                                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                                    Discard Changes
                                </h3>
                                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                                    Are you sure you want to discard this FAQ? All unsaved changes will be lost.
                                </p>

                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                    <Button
                                        type="primary"
                                        block
                                        style={{
                                            backgroundColor: "#41787C",
                                            borderColor: "#41787C",
                                            height: 40,
                                            fontWeight: 500,
                                        }}
                                        onClick={() => {
                                            setIsDiscardModalOpen(false);
                                            navigate(-1);
                                        }}
                                    >
                                        Discard
                                    </Button>
                                    <Button
                                        block
                                        style={{
                                            backgroundColor: "#f2f2f2",
                                            border: "none",
                                            color: "#333",
                                            height: 40,
                                            fontWeight: 500,
                                        }}
                                        onClick={() => setIsDiscardModalOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Modal>
                            <Col xs={24} sm={4} md="auto">
                                <Button
                                    type="primary"
                                    className="submit-btn btn-publish"
                                    style={{ width: "100%" }}
                                    onClick={async () => {
                                        try {
                                            await form.validateFields();
                                            setIsPublishModalOpen(true);
                                        } catch (error) {
                                            messageApi.error("Mandatory fields can’t be empty");
                                        }
                                    }}
                                >
                                    Submit
                                </Button>
                            </Col>
                        </>
                    )}

                </Row>
            </Form>

            {/* Publish Confirmation Modal */}
            <Modal
                open={isPublishModalOpen}
                onCancel={() => setIsPublishModalOpen(false)}
                footer={null}
                centered
                closable={false}
                bodyStyle={{
                    textAlign: "center",
                    padding: "24px 16px",
                    borderRadius: 12,
                }}
            >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                    Confirm Publish
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                    Are you sure you want to publish this FAQ?
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Button
                        type="primary"
                        block
                        style={{
                            backgroundColor: "#41787C",
                            borderColor: "#41787C",
                            height: 40,
                            fontWeight: 500,
                        }}
                        onClick={() => {
                            setIsPublishModalOpen(false);
                            handleSave();
                        }}
                    >
                        Yes
                    </Button>
                    <Button
                        block
                        style={{
                            backgroundColor: "#f2f2f2",
                            border: "none",
                            color: "#333",
                            height: 40,
                            fontWeight: 500,
                        }}
                        onClick={() => setIsPublishModalOpen(false)}
                    >
                        No
                    </Button>
                </div>
            </Modal>

            {/* Preview Drawer */}
            {previewVisible && (
                <>
                    {/* Overlay */}
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            zIndex: 1000,
                        }}
                        onClick={() => setPreviewVisible(false)}
                    />

                    {/* Drawer */}
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            right: 0,
                            width: window.innerWidth < 768 ? "100%" : "420px", // full width on mobile
                            height: "100vh",
                            backgroundColor: "rgba(0, 0, 0, 0.4)",
                            boxShadow: "-2px 0 8px rgba(0,0,0,0.3)",
                            zIndex: 1001,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {/* Scrollable Preview Container */}
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                width: "100%",
                                padding: "1rem",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                boxSizing: "border-box",
                            }}
                        >
                            <FaqMobilePreviewPage
                                data={previewData}
                                onClose={() => setPreviewVisible(false)}
                            />
                        </div>

                        {/* Fixed Close Button */}
                        <div
                            style={{
                                position: "fixed",
                                bottom: "20px",
                                right: window.innerWidth < 768 ? "50%" : "calc(420px / 2)",
                                transform:
                                    window.innerWidth < 768
                                        ? "translateX(50%)"
                                        : "translateX(50%)",
                                zIndex: 1102,
                                display: "flex",
                                justifyContent: "center",
                                width: "fit-content",
                            }}
                        >
                            <Button
                                type="primary"
                                onClick={() => setPreviewVisible(false)}
                                style={{ ...styles.whiteButton, width: 150, color: "black" }}
                            >
                                Close Preview
                            </Button>
                        </div>
                    </div>
                </>
            )}


        </>
    );
};

export default FaqCreateForm;

const styles: { [key: string]: React.CSSProperties } = {
    Questionlabel: {
        color: '#3E767A',
        fontSize: '18px',
        marginBottom: '7px',
        fontWeight: 630,
        lineHeight: '24px',
    },
};

